<script setup>
import { onMounted, ref } from "vue";
import { gsap } from "gsap";
import spaces from "@/assets/data/spaces.js";

const route = useRoute();
const { t } = useI18n();

const currentTestimonial = ref(0);
const changeTestimonial = (val) => {
  currentTestimonial.value = val;
};

const key = route.params.id;

const { locale } = useI18n();

const query = gql`
  query CustomPages {
    pages(where: {language: ${locale.value
      .toString()
      .toUpperCase()}, title: "${key}"}) {
    edges {
      node {
        id
        title
        spacesconfig {
          img {
            id
            mediaItemUrl
          }
          price
          copy
          heroimgh {
            id
            mediaItemUrl
          }
        }

      }
    }
  }

  }

`;

const variables = { limit: 5 };
const { data } = await useAsyncQuery(query, variables);

const imgList = data?.value.pages?.edges[0]?.node?.spacesconfig?.img ? data?.value.pages?.edges[0]?.node?.spacesconfig?.img?.map((i) => {
  return i.mediaItemUrl;
}) : [];

const price = data?.value.pages?.edges[0].node.spacesconfig.price;

const copy = data?.value.pages?.edges[0].node.spacesconfig.copy;

const heroimg =
  data?.value.pages?.edges[0].node.spacesconfig.heroimgh.mediaItemUrl;

const cursor = ref("");
const cursorStyle = ref({});
const cursorOnClick = ref(() => {});

const updateWidth = () => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", { duration: 0.1, ease: "power3" });
    yTo.value = gsap.quickTo("#cursor", "y", { duration: 0.1, ease: "power3" });
  }
};

const setCursor = (value, onClick, style) => {
  cursor.value = value;
  if (cursorOnClick.value !== onClick) {
    window.document.removeEventListener("click", cursorOnClick.value);
    cursorOnClick.value = onClick;
    window.document.addEventListener("click", cursorOnClick.value);
  }
  if (cursorStyle.value !== style.value) {
    cursorStyle.value = style;
  }
};

const title = computed(() => {
  var t = spaces.find((e) => e.key === route.params.id)?.title[locale.value];

  return t;
});

const width = ref(null);

const xTo = ref(null);
const yTo = ref(null);

const mouseMove = (e) => {
  if (width.value > 1024 && xTo.value && yTo.value) {
    xTo.value(e.pageX);
    yTo.value(e.pageY);
  }
};

onMounted(() => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", {
      duration: 0.1,
      ease: "power3",
    });
    yTo.value = gsap.quickTo("#cursor", "y", {
      duration: 0.1,
      ease: "power3",
    });
  }

  window.addEventListener("resize", updateWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
  window.document.removeEventListener("click", cursorOnClick.value);
});

const item = spaces.find((e) => e.key === route.params.id);

useHeadMeta({
  title: `${spaces.find((e) => e.key === route.params.id)?.seoTitle}`,
  description: `${spaces.find((e) => e.key === route.params.id)?.seoDescription}`,
  url: route.fullPath,
  image:
    data?.value.pages?.edges[0].node.spacesconfig.heroimgh.mediaItemUrl,
  alt: "Opero",
  breadcrumbs: [
		{
			name: t("spaces.meta.title"),
			item: route.fullPath.split("/").slice(0, -1).join("/"),
		},
	],
}, [{
  "@id": "#meeting-room",
  "@type": "Room",
  "name": item.title[locale.value],
  "containedInPlace": {
    "@id": "#company"
  },
  "offers": [
    {
      "@type": "Offer",
      "name": "Daytime rate",
      "description": "Meeting room rental from 8:00 AM to 6:00 PM",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "10",
        "priceCurrency": "USD",
        "unitText": "HOUR"
      }
    },
    {
      "@type": "Offer",
      "name": "Evening rate",
      "description": "Meeting room rental from 6:00 PM to midnight",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "20",
        "priceCurrency": "USD",
        "unitText": "HOUR"
      }
    }
  ]
}]);
</script>

<template>
  <div class="overflow-hidden" @mousemove="mouseMove">
    <div v-if="data" class="spaces-hero-detail">
      <HeroWithImage
        :set-cursor="setCursor"
        :title-image="heroimg"
        :title="title"
        :width="typeof spaces.find((e) => e.key === route.params.id)?.width === 'object' ? spaces.find((e) => e.key === route.params.id)?.width[$i18n.locale] : spaces.find((e) => e.key === route.params.id)?.width"
      />
      <div
        class="flex justify-center"
        @mouseenter="
          setCursor(null, null, {
            'font-size': '14px',
            background: '#FFC800',
            width: '25px',
            height: '25px',
            'z-index': '-1',
          })
        "
      >
        <div class="max-w-7xl">
          <SpaceDetail
            :space="spaces.find((e) => e.key === route.params.id)"
            :price="price"
            :copy="copy"
          >
            <div v-if="imgList.length">
              <ImageCarousel
                id="carouselid"
                :images="imgList"
                :current-image="currentTestimonial"
                :change-testimonial="changeTestimonial"
                :set-cursor="setCursor"
                class="!p-0"
              />

              <div class="carousel-bubbles flex justify-center">
                <div class="max-w-7xl">
                  <BubblesNavigationPaper
                    :height="50"
                    :width="40 * imgList.length"
                    start-width="50"
                    :testimonials="imgList"
                    :change-testimonial="changeTestimonial"
                    :current-selected="currentTestimonial"
                  />
                </div>
              </div>
            </div>
          </SpaceDetail>
        </div>
      </div>
      <BecomeMember :set-cursor="setCursor" />
      <Footer :set-cursor="setCursor" />
      <div
        id="cursor"
        class="hidden pointer-events-none cursor-auto lg:cursor-none lg:flex"
        :style="cursorStyle"
        :class="{
          smallCursor: cursor === null,
          hidden: !width || width < 1024,
        }"
        v-html="cursor"
      ></div>
    </div>
  </div>
</template>

<style lang="scss">
.spaces-hero-detail {
  .hero-container {
    // max-height: 900px;
    cursor: auto;
  }
}
.spaces-detail {
  img {
    max-height: 700px;
  }
}

#cursor {
  position: absolute;
  top: 0;
  left: -10px;
  width: 25px;
  height: 25px;
  font-weight: 400;

  justify-content: center;
  align-items: center;
  background-color: $color-primary;
  border-radius: 50%;
  z-index: 1000;
  cursor: none;
  transition:
    width 0.3s,
    height 0.3s,
    background-color 0.3s;
  transform: translate(-50%, -50%) translate(-150px, -150px);
  //pointer-events: none;
  text-align: center;
  display: none;
  @media screen {
    @media (pointer: fine) {
      display: flex;
    }
  }
}

.carousel-bubbles {
  @media all and (max-width: 900px) {
    display: none;
  }
}

.smallCursor {
  width: 20px;
  height: 20px;
  background-color: $color-primary;
}
</style>
